import {useCallback, useMemo} from 'react';

import {envIsDev} from '../../../config';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {isSystemMetric} from '../../../util/metrics';
import {type Line} from '../../../util/plotHelpers/types';
import {useIsFullScreenPanel} from '../../Panel/hooks/useIsFullScreenPanel';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {type LinePlotZoomDragMetadata} from '../../vis/brushZoomMetadata';
import {type AxisZoomIntent} from '../types';

export const LINE_PLOT_USAGE_ZOOM_ACTION = 'line_plot.usage.zoom';
// Local debugging switch. Keep env gated so this cannot log in production.
// eslint-disable-next-line no-constant-binary-expression
const DEBUG_LINE_PLOT_USAGE_RUM = false && envIsDev;
// Runtime-local per-panel zoom count. Intentionally resets on page reload.
const panelZoomCounts = new Map<string, number>();

export type LinePlotZoomUsageSource = 'config' | 'drag';
export type LinePlotZoomUsageType = 'x-axis' | 'y-axis' | 'xy-axis' | 'reset';

export type LinePlotZoomUsageEvent = {
  type: LinePlotZoomUsageType;
  source: LinePlotZoomUsageSource;
} & LinePlotZoomDragMetadata;

type UseLinePlotZoomUsageTrackerParams = {
  lines: Line[];
  metrics?: string[];
  xAxis: string | undefined;
};

type LinePlotZoomUsageContext = LinePlotZoomUsageEvent & {
  panelId: string;
  isInReport: boolean;
  isFullScreen: boolean;
  xAxis: string | undefined;
  metricCount: number;
  metricNames: string;
  isSystemMetric: boolean;
  zoomCount: number;
};

export function getZoomUsageType(
  intent?: AxisZoomIntent
): LinePlotZoomUsageType {
  if (intent?.shouldZoomX && intent?.shouldZoomY) {
    return 'xy-axis';
  }
  if (intent?.shouldZoomX) {
    return 'x-axis';
  }
  if (intent?.shouldZoomY) {
    return 'y-axis';
  }
  return 'reset';
}

export function getConfigZoomUsageType({
  axis,
  max,
  min,
}: {
  axis: 'x' | 'y';
  max: number | null | undefined;
  min: number | null | undefined;
}): LinePlotZoomUsageType {
  if (min == null && max == null) {
    return 'reset';
  }

  return axis === 'x' ? 'x-axis' : 'y-axis';
}

export function getLinePlotMetricNames({
  lines,
  metrics,
}: {
  lines: Line[];
  metrics?: string[];
}): string[] {
  const metricNames = metrics != null && metrics.length > 0 ? metrics : [];
  if (metricNames.length > 0) {
    return [...new Set(metricNames)];
  }

  return [
    ...new Set(
      lines
        .map(line => line.metricName)
        .filter((metricName): metricName is string => metricName != null)
    ),
  ];
}

export function getIsSystemMetricPanel(metricNames: string[]): boolean {
  return metricNames.some(isSystemMetric);
}

export function getNextPanelZoomCount(panelId: string): number {
  const nextCount = (panelZoomCounts.get(panelId) ?? 0) + 1;
  panelZoomCounts.set(panelId, nextCount);
  return nextCount;
}

function logDebugLinePlotUsageRum(
  actionName: string,
  context: LinePlotZoomUsageContext
) {
  if (envIsDev && DEBUG_LINE_PLOT_USAGE_RUM) {
    console.log('[LinePlot RUM]', actionName, context);
  }
}

export function useLinePlotZoomUsageTracker({
  lines,
  metrics,
  xAxis,
}: UseLinePlotZoomUsageTrackerParams): (event: LinePlotZoomUsageEvent) => void {
  const panelId = useStablePanelId();
  const isInReport = useIsInLoadedReportContext();
  const {isFullScreen} = useIsFullScreenPanel();

  const metricNames = useMemo(
    () => [...getLinePlotMetricNames({lines, metrics})].sort(),
    [lines, metrics]
  );
  const isSystemMetricPanel = getIsSystemMetricPanel(metricNames);

  return useCallback(
    (event: LinePlotZoomUsageEvent) => {
      try {
        const context = {
          ...event,
          isFullScreen,
          isInReport,
          isSystemMetric: isSystemMetricPanel,
          metricCount: metricNames.length,
          metricNames: metricNames.join(', '),
          panelId,
          xAxis,
          zoomCount: getNextPanelZoomCount(panelId),
        };

        logDebugLinePlotUsageRum(LINE_PLOT_USAGE_ZOOM_ACTION, context);
        recordRumAction(LINE_PLOT_USAGE_ZOOM_ACTION, context);
      } catch {
        // Observability must never interrupt line plot interactions.
      }
    },
    [isFullScreen, isInReport, isSystemMetricPanel, metricNames, panelId, xAxis]
  );
}
